<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { get } from 'svelte/store';
	import {
		AdvancedCardV2,
		Alert,
		Button,
		CardGrid,
		Icon,
		LabelV2,
		Text,
	} from '@Shelly/shelly-design-system/index';
	import BadgeContainer from '@Shelly/shelly-design-system/helpers/BadgeContainer/BadgeContainer.svelte';
	import { format } from '../../services/i18n';

	import { dashboards, dashboardTabs, dashesSearchValue } from 'src/stores/features/dashboards';
	import { dashEdit } from 'src/stores/ui/edit_lists';
	import { querystring } from 'svelte-spa-router';
	import { push } from 'svelte-spa-router';
	import {
		checkIsCameraMonitoringTab,
		checkIsNonDashboardTab,
		requestReorderDashboards,
		sortBy,
	} from '../Lists/list-helpers';
	import { mapDashItemsToDashWidgets, mapDashToAvatar } from './dash-helpers';
	import { getCameraMonitoringCount } from '../Camera/helpers/camera-helpers';
	import { save_home } from '../../helpers/home_helpers';
	import { home_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import AddEditDashboardNewLayout from '../Lists/Dashboards/AddEditDashboardNewLayout.svelte';

	const flipDurationMs = 0;
	let dragDisabled = true;
	let indexForLabel = 0;
	let selectedDash;
	let showAddEditModal = false;

	$: showAlert = $home_data.showDashAlert?.show ?? true;

	const handleAlertDismiss = async () => {
		await save_home('showDashAlert', { show: false });
	};

	//#region Reorder
	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
		handleEndDrag();
	};

	const handleStartDrag = () => {
		indexForLabel = -1;
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		indexForLabel = 0;
		dragDisabled = true;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		let editedList = {};

		e.detail.items.map((v, i) => {
			delete v?.isDndShadowItem;

			editedList[v.id] = {
				...v,
				position: i,
			};
		});

		if (Object.keys(editedList).length === 0) return;

		items = [...e.detail.items];

		handleEndDrag();

		await requestReorderDashboards(editedList);
	};
	//#endregion Reorder

	$: dashboardItems = $dashboardTabs.filter((dash) =>
		$dashesSearchValue.trim().length === 0
			? true
			: dash.text.toLowerCase().includes($dashesSearchValue.toLowerCase()),
	);

	$: items = $dashEdit ? items : [...dashboardItems];

	$: dndSettings = {
		items,
		flipDurationMs: flipDurationMs,
		dragDisabled: dragDisabled,
	};

	let searchParams;

	onMount(() => {
		searchParams = new URLSearchParams($querystring);

		if (searchParams?.get('edit')) {
			requestAnimationFrame(() => {
				dashEdit.set(true);
			});
		}
	});

	onDestroy(() => {
		dashEdit.set(false);
	});
	$: dndShadowItemIndex = items?.findIndex((el) => el?.isDndShadowItem);

	const getDashboardCardSubtitle = (
		dash: { id: string | number; route?: string },
		currentDash: unknown,
	): string => {
		const _format = get(format);

		if (checkIsCameraMonitoringTab(dash)) {
			const camerasCount = getCameraMonitoringCount(get(home_data), get(devices));
			return _format('cameras_count_subtitle', {
				default: '{camerasCount} cameras',
				values: { camerasCount },
			});
		}

		const widgetsCount = [...mapDashItemsToDashWidgets(currentDash, dash.id)]
			.sort((x, y) => sortBy(x, y, 'position'))
			.filter((item) => item?.cards?.length > 0).length;

		return _format('widgets_count_subtitle', {
			default: '{widgetsCount} widgets',
			values: { widgetsCount },
		});
	};
</script>

{#if items.length === 0 && !!$dashesSearchValue}
	<div class="d-flex flex-direction-column gap-m pt-md-l pt-sm-m width-full align-items-center">
		<Icon size="l" icon="fal fa-magnifying-glass" />
		<div class="d-flex flex-direction-column align-items-center gap-s">
			<Text element="h1"
				>{$format('label_no_dash_found', { default: 'No such dashboards found.' })}</Text
			>
			<Text class="text-color-muted"
				>{$format('check_the_spelling_or_try_new_search', {
					default: 'Check the spelling or try a new search.',
				})}</Text
			>
		</div>
	</div>
{:else if items.length === 0 && !$dashesSearchValue}
	<div class="d-flex flex-direction-column gap-m pt-md-l pt-sm-m width-full align-items-center">
		<Icon size="l" icon="fal fa-objects-column" />
		<div class="d-flex flex-direction-column align-items-center gap-s">
			<Text class="text-center" element="h1">
				{$format('label_no_dashboards_created', { default: 'No dashboards created yet' })}
			</Text>
			<Text class="text-center text-color-muted">
				{$format('label_add_first_dash_to_start_heading', {
					default: 'Add your first dashboard to get started.',
				})}
			</Text>
		</div>
		<Button
			on:click={() => {
				showAddEditModal = true;
			}}
		>
			{$format('text_sub_title_add_dashboard', { default: 'Add dashboard' })}
		</Button>
	</div>
{:else}
	{#if showAlert}
		<Alert
			isHeadingBold
			class="mb-m"
			customIcon="text-primary fas fa-star"
			heading={$format('label_first_dash_landing_heading', {
				default: 'First dashboard as landing page',
			})}
			message={$format('label_dash_landing_description', {
				default:
					'Keep in mind that the first dashboard in the list will be the page that you land on when you open the app.',
			})}
			dismissable
			on:dismissable_click={handleAlertDismiss}
		/>
	{/if}
	{#key showAlert}
		<CardGrid
			editMode={$dashEdit}
			xl={350}
			lg={290}
			sm={270}
			{dndSettings}
			on:consider={handleReorder}
			on:finalize={handleReorderEnd}
		>
			{#each items as dash, index (dash)}
				{@const currentDashs = $dashboards[dash.id] ?? dash}

				<div class:dnd-placeholder={dash.isDndShadowItem}>
					<AdvancedCardV2
						aria-label="Advanced Card"
						isFocusable
						title={dash.text}
						editMode={$dashEdit}
						selected={selectedDash === index}
						hideSlotRightBadge={indexForLabel !== index}
						avatar={mapDashToAvatar(dash, 'color-base')}
						subtitle={{
							value: getDashboardCardSubtitle(dash, currentDashs),
							bold: false,
							type: 'muted',
							semiBold: false,
						}}
						on:click={() => {
							if (!$dashEdit)
								push(
									checkIsNonDashboardTab(dash)
										? `/dash${dash.route}`
										: `/dash/${dash.id}`,
								);
							selectedDash = index;
						}}
						on:handle-drag-start={handleStartDrag}
						on:handle-drag-end={handleEndDrag}
					>
						<LabelV2
							backgroundColor="--color-base"
							slot="rightBadge"
							size="s"
							icon={'text-primary fas fa-star'}
							noRadiusType="first"
						/>
					</AdvancedCardV2>
					{#if dndShadowItemIndex === 0 && dash.isDndShadowItem}
						<BadgeContainer type="right">
							<LabelV2
								backgroundColor="--color-base"
								size="s"
								noRadiusType="first"
								icon={'text-primary fas fa-star'}
							/>
						</BadgeContainer>
					{/if}
				</div>
			{/each}
		</CardGrid>
	{/key}
{/if}

<AddEditDashboardNewLayout bind:dashboardsEdit={showAddEditModal} />
